import React, { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import deepEqual from 'fast-deep-equal';
import { Box, Paper, Typography, Divider, Button, IconButton, Collapse } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CollapsiblePanel from '../CollapsiblePanel';
import { de } from 'date-fns/locale';
import { perfLog, perfNow } from '../../../utils/perfLog.js';

// Create a context for filter operations
export const FilterContext = createContext();

export const useFilterContext = () => useContext(FilterContext);

export default function DataFilter({
  data = [],
  children,
  title = "Filter Data",
  onFilteredDataChange = null,
  onFiltersDescriptionChange = null,
  showFilterSummary = true,
  initialFilters = {},
  defaultCollapsed = false
}) {
  // Store the original data
  const [originalData, setOriginalData] = useState(data);

  // Store filtered data
  const [filteredData, setFilteredData] = useState(data);

  // Store active filters
  const [activeFilters, setActiveFilters] = useState(initialFilters);

  // Store clear handlers for each filter
  const [clearHandlers, setClearHandlers] = useState({});

  // Store filter description handlers
  const [descriptionHandlers, setDescriptionHandlers] = useState({});
  const [filterLabels, setFilterLabels] = useState({});

  // Track number of active filters
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  // Use ref to store the callback to avoid unnecessary effect runs
  const onFilteredDataChangeRef = useRef(onFilteredDataChange);
  const onFiltersDescriptionChangeRef = useRef(onFiltersDescriptionChange);

  // Track the last values we sent to the parent so we can content-compare
  // (NOT reference-compare against a stale closure) and avoid re-firing the
  // callback every render. Without these, the apply-filters useEffect below
  // produces fresh array references every run, parent state setters get
  // called with new refs each time, parent re-renders cascade back into the
  // child filters, and the cycle never breaks.
  const lastNotifiedFilteredDataRef = useRef(undefined);
  const lastNotifiedDescriptionsRef = useRef(undefined);

  // Diagnostic-only: counts how many times the apply-filters effect runs, so a
  // profiling run can see the deepEqual cost multiplied by re-fires (e.g. once
  // per streamed page). Not read by any logic; safe to remove after profiling.
  const applyCountRef = useRef(0);

  useEffect(() => {
    onFilteredDataChangeRef.current = onFilteredDataChange;
  }, [onFilteredDataChange]);

  useEffect(() => {
    onFiltersDescriptionChangeRef.current = onFiltersDescriptionChange;
  }, [onFiltersDescriptionChange]);

  // Update original data if the data prop changes.
  //
  // Content-gate (deepEqual) rather than a reference/length check: the data
  // prop arrives as a FRESH array reference even when the underlying rows are
  // unchanged (flattenInfiniteData always builds a new array; only the
  // calling page's useMemo — not every consumer — stabilizes it). A
  // reference check fires setOriginalData on every such re-render, which swaps
  // the originalData reference and re-triggers the apply-filters effect below
  // (it depends on originalData) — the render/stability loop fixed in May
  // (filter-component-stability skill). deepEqual is the proven gate.
  useEffect(() => {
    if (!deepEqual(originalData, data)) {
      setOriginalData(data);
    }
  }, [data, originalData]);

  // Register a filter for a specific field with optional clear handler
  const registerFilter = useCallback((field, label, filterFn, clearHandler = null, descriptionHandler = null) => {
    setActiveFilters(prev => {
      // Only update if the filter actually changed
      if (prev[field] === filterFn) {
        return prev;
      }
      return {
        ...prev,
        [field]: filterFn
      };
    });

    setFilterLabels(prev => ({
      ...prev,
      [field]: label
    }));

    // Register clear handler if provided
    if (clearHandler) {
      setClearHandlers(prev => ({
        ...prev,
        [field]: clearHandler
      }));
    }

    // Create default description handler with the current label
    // This ensures we use the label passed to this function call
    if (!descriptionHandler) {
      descriptionHandler = () => `${label}: (filtered)`;
    }

    setDescriptionHandlers(prev => ({
      ...prev,
      [field]: descriptionHandler
    }));
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    // Call all clear handlers first
    Object.values(clearHandlers).forEach(handler => {
      if (typeof handler === 'function') {
        handler();
      }
    });

    // Then clear the filters and handlers
    setActiveFilters({});
    setClearHandlers({});
    setDescriptionHandlers({});
  }, [clearHandlers]);

  // Clear a specific filter
  // Refs that mirror the live state so context callbacks (clearFilter,
  // describeActiveFilters) can stay STABLE — useCallback with [] deps —
  // while still reading the latest values when invoked.
  //
  // Without this, those callbacks had clearHandlers / descriptionHandlers in
  // their useCallback deps. Those are state objects that mutate on EVERY
  // registerFilter call (which always recreates them via spread). Each new
  // ref propagated to anyone depending on the context callbacks: filter
  // components like TriStateBooleanFilter that include `clearFilter` in
  // their useEffect deps re-fired on every register, called clearFilter
  // again, mutated clearHandlers, recreated clearFilter, refired the effect…
  // infinite loop on a default-state tri-state filter.
  const clearHandlersRef = useRef(clearHandlers);
  const descriptionHandlersRef = useRef(descriptionHandlers);
  useEffect(() => { clearHandlersRef.current = clearHandlers; }, [clearHandlers]);
  useEffect(() => { descriptionHandlersRef.current = descriptionHandlers; }, [descriptionHandlers]);

  const clearFilter = useCallback((field) => {
    // Call the specific clear handler if it exists
    const handlers = clearHandlersRef.current;
    if (handlers[field] && typeof handlers[field] === 'function') {
      handlers[field]();
    }

    // Remove the filter and handler
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });

    setClearHandlers(prev => {
      const newHandlers = { ...prev };
      delete newHandlers[field];
      return newHandlers;
    });

    setDescriptionHandlers(prev => {
      const newDescriptionHandlers = { ...prev };
      delete newDescriptionHandlers[field];
      return newDescriptionHandlers;
    });
  }, []);

  // Describe the filters that are actually narrowing the data.
  //
  // Every mounted filter registers a description handler, whether or not it is
  // narrowing anything — so this cannot just list them all. A handler returns
  // a falsy description to say "I am registered but not narrowing", and those
  // are skipped: a filter set to its show-everything state does not belong in
  // a list captioned "Filters Applied" on a court-facing PDF or export.
  //
  // A filter that registers no handler at all is a different case — the field
  // is filtered but we cannot say how — and it still gets surfaced:
  // registerFilter substitutes a `${label}: (filtered)` default handler, whose
  // return is truthy and so passes the check below. The typeof guard here is
  // therefore belt-and-braces, not the path that produces "(filtered)".
  //
  // Stays useCallback with EMPTY deps per the filter-component-stability
  // standard (rule 2); state is read through descriptionHandlersRef, which a
  // sibling effect mirrors. Do not add deps here.
  const describeActiveFilters = useCallback(() => {
    const descriptions = [];
    Object.entries(descriptionHandlersRef.current).forEach(([field, handler]) => {
      if (typeof handler !== 'function') {
        descriptions.push(`${field}: (filtered)`);
        return;
      }
      const desc = handler();
      if (desc) descriptions.push(desc);
    });
    return descriptions;
  }, []);

  // Apply filters whenever active filters or original data changes
  useEffect(() => {
    const _t0 = perfNow();
    let result = [...originalData];

    // Apply each filter
    Object.entries(activeFilters).forEach(([field, filterFn]) => {
      if (filterFn) {
        result = result.filter(item => filterFn(item));
      }
    });

    // Compute the "did the filtered content change?" check ONCE and reuse it
    // for both the setFilteredData guard and the parent notification. These
    // compared result against lastNotifiedFilteredDataRef — the same value —
    // so doing deepEqual(result, <that>) twice over a 31k-row array was pure
    // waste; one comparison is enough to drive both.
    const resultChanged = !deepEqual(lastNotifiedFilteredDataRef.current, result);

    // Only update if the filtered data actually changed
    setFilteredData(prevFiltered => {
      if (prevFiltered === lastNotifiedFilteredDataRef.current) {
        // prevFiltered IS the last-notified value; resultChanged already
        // tells us whether it differs from result.
        return resultChanged ? result : prevFiltered;
      }
      if (deepEqual(prevFiltered, result)) {
        return prevFiltered;
      }
      return result;
    });

    // Count the filters that are actually narrowing the data — the same rule
    // describeActiveFilters uses. Pick-list filters stay registered with a
    // show-everything filterFn and a falsy description while untouched;
    // counting them kept "Showing N of N items (6 filters)" and Clear All on
    // screen with nothing filtered, and put both straight back after Clear
    // All, so the button looked broken (Trello n4Wlq5zy). A filter with no
    // description handler (e.g. initialFilters) still counts.
    const count = Object.entries(activeFilters).filter(([field, filterFn]) => {
      if (!filterFn) return false;
      const handler = descriptionHandlersRef.current[field];
      return typeof handler !== 'function' || Boolean(handler());
    }).length;
    setActiveFilterCount(count);

    // Notify parent only when the filtered result has actually changed in
    // CONTENT. Reuses resultChanged above (deepEqual against the last notified
    // value) instead of a second full-array deepEqual. The original code here
    // was `result !== filteredData`, which compared a fresh array against a
    // stale closure value — always-true, so the callback always fired and
    // cascaded through parent setState into needless re-renders.
    if (onFilteredDataChangeRef.current && resultChanged) {
      lastNotifiedFilteredDataRef.current = result;
      onFilteredDataChangeRef.current(result);
    }

    // Notify parent component of filter descriptions if callback provided.
    // Same content-compare guard as the filteredData notification above —
    // describeActiveFilters() returns a fresh array each call, so without
    // this, parent's setFiltersDescription fires every apply-filters run with
    // a new ref, parent re-renders, cascading back into another apply-filters
    // run, and the cycle never breaks.
    if (onFiltersDescriptionChangeRef.current) {
      const descriptions = describeActiveFilters();
      const descriptionsChanged = !deepEqual(lastNotifiedDescriptionsRef.current, descriptions);
      if (descriptionsChanged) {
        lastNotifiedDescriptionsRef.current = descriptions;
        onFiltersDescriptionChangeRef.current(descriptions);
      }
    }

    // Diagnostic-only (gated, primitives only): cost of the full-array filter +
    // deepEqual pass, and how many times it re-fires.
    applyCountRef.current += 1;
    perfLog('filter.apply', {
      call: applyCountRef.current,
      inRows: originalData.length,
      outRows: result.length,
      filters: count,
      ms: +(perfNow() - _t0).toFixed(1),
    });
  }, [activeFilters, originalData]); // Removed filteredData dependency to prevent loops

  // Context value to be provided to children - memoize to prevent unnecessary re-renders
  const contextValue = {
    originalData,
    filteredData,
    registerFilter,
    clearFilter,
    activeFilters
  };

  const titleElement = (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      columnGap: 2,
      rowGap: 1,
      flex: 1,
      mr: 2,
      minWidth: 0,  // allow inner flex children to shrink/truncate
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: 2,
        rowGap: 0.5,
        minWidth: 0,
        flex: 1,
      }}>
        <FilterListIcon sx={{ flexShrink: 0 }} />
        <Typography
          variant="subtitle1"
          component="h3"
          sx={{ fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {title}
        </Typography>
        {showFilterSummary && activeFilterCount > 0 && (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Showing {filteredData.length} of {originalData.length} items
              ({activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''})
            </Typography>
            {describeActiveFilters().map((desc, idx) => (
              <Typography
                key={idx}
                variant="body2"
                color="text.secondary"
                sx={{
                  minWidth: 0,
                  flexShrink: 1,
                  flexBasis: 'auto',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {desc}
              </Typography>
            ))}
          </>
        )}
      </Box>
      {activeFilterCount > 0 && (
        <Button
          size="small"
          onClick={clearAllFilters}
          variant="outlined"
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          Clear All
        </Button>
      )}
    </Box>
  );

  return (
    <FilterContext.Provider value={contextValue}>
      <CollapsiblePanel titleElement={titleElement} defaultCollapsed={defaultCollapsed}>
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          '& > *': {
            flexGrow: 1,
            minWidth: 200,
            maxWidth: 350
          }
        }}>
          {children}
        </Box>
      </CollapsiblePanel>

    </FilterContext.Provider>
  );
}